<script lang="ts">
  /**
   * The shell's one button shape, in three weights:
   *
   * - `filled` — the one thing a screen is for (Create game, Join). Accent
   *   fill; there is at most one per screen.
   * - `light` — an action that belongs to something already on screen
   *   (Continue inside a game card, Play today inside a challenge card): an
   *   accent outline on whatever it sits on, so it never competes with the
   *   filled one (the owner's round-2 review: "make New game more distinct
   *   from continuing a game").
   * - `plain` — the way out of a question (Not now).
   *
   * `busy` keeps the button's size and says so to assistive technology; a
   * busy or disabled button does not call `onclick`.
   *
   * Since 1.2.0:
   *
   * - `tone="danger"` paints any weight in the danger colour instead of the
   *   accent (Delete, Leave the room) — the fill, the outline or the words.
   * - `size="sm"`: 2.25rem tall to the eye, the target still 2.75rem (a
   *   row's own small action).
   * - `type="submit"`: the button submits the form it is in (Enter in a
   *   field does too); `onclick` is then optional. TwinTen's sign-in form
   *   buttons are a `filled` and a `light` pill with `equal`.
   * - `equal`: siblings in a row share it equally (`flex: 1 1 0`). Since
   *   1.3.0 they come out exactly equal whatever their weights: with
   *   `equal` every weight wears the light pill's 2px border (transparent
   *   on a filled or plain pill), so a filled and a light pill sharing the
   *   row are the same box (in 1.2.0 the light one was 4px wider). Any other
   *   pill (`grow`, or one that hugs its label) is unchanged.
   *
   * Since 1.7.0, `name`: the button's accessible name when its words alone
   * do not say enough ("Accept" on a row, named "Accept Robin's request").
   * Begin it with the visible words, so a person who says what they see
   * still reaches it (WCAG 2.5.3). A button with a glyph and no words is an
   * `IconButton`.
   *
   * Since 1.8.0, `href`: the pill is a real link (`<a href>`) that looks
   * exactly the same, for a way on that is a place (a lesson's page, the
   * next unit), so the browser's own link behaviours work (open in a new
   * tab, copy the address, a server-rendered page that works before it
   * hydrates). On a link, a plain primary click calls `onclick` (with no
   * argument, as on a button) and then `onnavigate(event)`, links only: the
   * place to call `event.preventDefault()` and route yourself (or the
   * browser loads the address). A modified or non-primary click (Ctrl, Cmd,
   * Shift, Alt, the middle button: a new tab, a new window) is the
   * browser's alone and calls neither.
   * `current` marks the link to the page or step being shown
   * (`aria-current`; `true` is `page`). A disabled or busy link has no
   * `href` (it goes nowhere and leaves the Tab order, as a disabled button
   * does) and `aria-disabled`; a disabled one is faded, a busy one keeps its
   * look and shows the spinner, as a busy button does. `type` means nothing
   * on a link.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly label: string;
    /** Required unless `type` is `submit`. */
    readonly onclick?: (() => void) | undefined;
    readonly variant?: 'filled' | 'light' | 'plain';
    readonly tone?: 'accent' | 'danger';
    readonly size?: 'sm' | 'md' | 'lg';
    readonly type?: 'button' | 'submit';
    /** Share the row equally with its siblings. */
    readonly equal?: boolean;
    /** A glyph before the label, drawn in `currentColor`. */
    readonly icon?: Snippet | undefined;
    readonly disabled?: boolean;
    readonly busy?: boolean;
    /** Stretch to the row's width (a card's Continue) rather than hugging the label. */
    readonly grow?: boolean;
    /** Marks the button as where focus lands when the sheet it is in opens (`Sheet`'s `[data-autofocus]`). */
    readonly autofocus?: boolean;
    readonly testid?: string | undefined;
    // ── 1.7.0 ──
    /** The accessible name, when the label alone does not say enough; begin it with the label. */
    readonly name?: string | undefined;
    // ── 1.8.0 ──
    /** A link instead of a button: a real `<a href>` that looks the same. */
    readonly href?: string | undefined;
    /** A link to what is being shown (`aria-current`); `true` is `page`. Links only. */
    readonly current?: boolean | 'page' | 'step' | 'location' | undefined;
    /**
     * Links only: a plain primary click, after `onclick`, with the event. Call
     * `event.preventDefault()` here to route yourself instead of loading `href`.
     */
    readonly onnavigate?: ((event: MouseEvent) => void) | undefined;
  }
  const {
    label,
    onclick,
    variant = 'filled',
    tone = 'accent',
    size = 'md',
    type = 'button',
    equal = false,
    icon,
    disabled = false,
    busy = false,
    grow = false,
    autofocus = false,
    testid,
    name,
    href,
    current,
    onnavigate,
  }: Props = $props();

  const ariaCurrent = $derived(current === true ? 'page' : current === false ? undefined : current);
  /** A link that cannot be followed right now: no address, so it goes nowhere. */
  const unlinked = $derived(disabled || busy);

  function handleClick(event: MouseEvent): void {
    if (disabled || busy) {
      event.preventDefault();
      return;
    }
    if (href === undefined) {
      onclick?.();
      return;
    }
    // A link (1.8.0) opened in a new tab or window, or saved (a modified or
    // non-primary click), is the browser's alone: neither callback runs.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    onclick?.();
    onnavigate?.(event);
  }
</script>

{#snippet content()}
  {#if busy}
    <span class="ui-pill-spinner" aria-hidden="true"></span>
  {:else if icon !== undefined}
    <span class="ui-pill-icon" aria-hidden="true">{@render icon()}</span>
  {/if}
  <span>{label}</span>
{/snippet}

{#if href !== undefined}
  <!-- A link (1.8.0). Without an address while it cannot be followed, so it
       goes nowhere and leaves the Tab order; `role="link"` keeps it a link. -->
  <a
    href={unlinked ? undefined : href}
    class="ui-pill ui-pill--{variant} ui-pill--{size}"
    class:ui-pill--danger={tone === 'danger'}
    class:ui-pill--grow={grow}
    class:ui-pill--equal={equal}
    class:ui-pill--off={disabled}
    role={unlinked ? 'link' : undefined}
    aria-disabled={unlinked ? 'true' : undefined}
    aria-busy={busy ? 'true' : undefined}
    aria-current={ariaCurrent}
    aria-label={name}
    data-autofocus={autofocus ? '' : undefined}
    data-testid={testid}
    onclick={handleClick}
  >
    {@render content()}
  </a>
{:else}
  <button
    {type}
    class="ui-pill ui-pill--{variant} ui-pill--{size}"
    class:ui-pill--danger={tone === 'danger'}
    class:ui-pill--grow={grow}
    class:ui-pill--equal={equal}
    {disabled}
    aria-busy={busy ? 'true' : undefined}
    aria-label={name}
    data-autofocus={autofocus ? '' : undefined}
    data-testid={testid}
    onclick={handleClick}
  >
    {@render content()}
  </button>
{/if}

<style>
  .ui-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--ui-space-2);
    min-height: 2.75rem;
    padding: 0.55rem 1.1rem;
    border-radius: 999px;
    font: var(--ui-text-label);
    font-weight: 800;
    line-height: 1.15;
    text-align: center;
  }

  /* A link pill (1.8.0) is the same box as a button pill. */
  a.ui-pill {
    box-sizing: border-box;
    text-decoration: none;
  }

  .ui-pill--lg {
    min-height: 3.1rem;
    padding: 0.85rem 1.5rem;
    font-size: 1.05rem;
  }

  .ui-pill--sm {
    position: relative;
    min-height: 2.25rem;
    padding: 0.35rem 0.85rem;
  }

  /* The target reaches 2.75rem whatever the pill looks like. */
  .ui-pill--sm::before {
    content: '';
    position: absolute;
    inset: -0.25rem 0;
  }

  .ui-pill--grow {
    flex: 1 1 auto;
  }

  .ui-pill--equal {
    flex: 1 1 0;
    min-width: 0;
  }

  /* Equal means equal (1.3.0): a flex item's padding and border are never
     shared out, so a light pill's 2px outline used to leave it 4px wider
     than a filled or plain one in the same `equal` row. There every weight
     wears the same 2px border, transparent where it has none to show (the
     fill paints under it). Any other pill (one that hugs its label, or
     `grow`) is exactly as in 1.2.0. */
  .ui-pill--equal.ui-pill--filled,
  .ui-pill--equal.ui-pill--plain {
    border: 2px solid transparent;
  }

  .ui-pill--filled {
    border: 0;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-pill--light {
    border: 2px solid var(--ui-accent);
    background: transparent;
    color: var(--ui-accent);
  }

  .ui-pill--plain {
    border: 0;
    background: transparent;
    color: var(--ui-accent);
  }

  .ui-pill--danger.ui-pill--filled {
    background: var(--ui-danger);
    color: var(--ui-on-danger);
  }

  .ui-pill--danger.ui-pill--light {
    border-color: var(--ui-danger);
    color: var(--ui-danger);
  }

  .ui-pill--danger.ui-pill--plain {
    color: var(--ui-danger);
  }

  .ui-pill:disabled,
  .ui-pill--off {
    opacity: 0.5;
  }

  .ui-pill:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-pill-icon {
    display: inline-flex;
    width: 1.1rem;
    height: 1.1rem;
  }

  .ui-pill-icon :global(svg) {
    width: 100%;
    height: 100%;
  }

  .ui-pill-spinner {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-right-color: transparent;
    animation: ui-pill-spin 0.8s linear infinite;
  }

  @keyframes ui-pill-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ui-pill-spinner {
      animation: none;
    }
  }
</style>
